import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Shipment RPC Contract
 *
 * Provides endpoints for fetching shipments and their related entities.
 */

const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const UUIDSchema = z.uuid();

/**
 * Shipment type enum
 */
export const ShipmentTypeSchema = z.enum(["incoming", "outgoing"]);

/**
 * Shipment status enum
 */
export const ShipmentStatusSchema = z.enum([
  "need_to_be_scheduled",
  "scheduled",
  "in_transit",
  "delivered",
  "cancelled",
]);

/**
 * Pagination schema
 */
export const ShipmentPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Related sales order summary
 */
export const ShipmentSalesOrderSummarySchema = z.object({
  id: UUIDSchema,
  internalId: z.int(),
  customerPurchaseOrderNumber: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  totalAmount: DecimalStringSchema.nullable().optional(),
  status: z.string().nullable().optional(),
});

/**
 * Related purchase order summary
 */
export const ShipmentPurchaseOrderSummarySchema = z.object({
  id: UUIDSchema,
  externalId: z.string(),
  internalId: z.int(),
  supplierName: z.string().nullable().optional(),
  totalAmount: DecimalStringSchema.nullable().optional(),
  status: z.string().nullable().optional(),
});

/**
 * Charge summary for shipment
 */
export const ShipmentChargeSummarySchema = z.object({
  id: UUIDSchema,
  categoryLabel: z.string().nullable(),
  amount: DecimalStringSchema,
  direction: z.enum(["DEBIT", "CREDIT"]),
});

/**
 * Base shipment schema
 */
export const ShipmentSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  type: ShipmentTypeSchema,
  status: ShipmentStatusSchema,
  origin: z.string(),
  destination: z.string(),
  scheduledAt: z.string(),
  departureAt: z.string().nullable().optional(),
  arrivalAt: z.string().nullable().optional(),
  weight: z.number().nullable().optional(),
  volume: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  originLoadId: z.string().nullable().optional(),
  originLoadNumber: z.string().nullable().optional(),
  originSystem: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ShipmentDestinationSummarySchema = z.object({
  sequence: z.int().positive(),
  salesOrderId: UUIDSchema,
  salesOrderInternalId: z.int(),
  orderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  destinationLabel: z.string(),
  destinationAddress: z.string().nullable(),
});

export const ShipmentChargeAllocationSummarySchema = z.object({
  salesOrderId: UUIDSchema,
  salesOrderInternalId: z.int(),
  orderNumber: z.string().nullable(),
  customerName: z.string().nullable(),
  destinationLabel: z.string().nullable(),
  amount: DecimalStringSchema,
  direction: z.enum(["DEBIT", "CREDIT"]),
});

export const ShipmentChargeBreakdownSchema = z.object({
  shipmentChargeId: UUIDSchema,
  categoryLabel: z.string().nullable(),
  amount: DecimalStringSchema,
  direction: z.enum(["DEBIT", "CREDIT"]),
  allocations: z.array(ShipmentChargeAllocationSummarySchema),
});

/**
 * List shipments input
 */
export const ListShipmentsInputSchema = z.object({
  type: ShipmentTypeSchema.optional(),
  status: z.array(ShipmentStatusSchema).optional(),
  scheduledAtFrom: z.string().optional(),
  scheduledAtTo: z.string().optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List shipments response
 */
export const ListShipmentsResponseSchema = z.object({
  shipments: z.array(ShipmentSchema),
  pagination: ShipmentPaginationSchema,
});

/**
 * Get shipment input
 */
export const GetShipmentInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get shipment response
 */
export const GetShipmentResponseSchema = z.object({
  shipment: ShipmentSchema.nullable(),
  salesOrders: z.array(ShipmentSalesOrderSummarySchema),
  purchaseOrders: z.array(ShipmentPurchaseOrderSummarySchema),
  orderedDestinations: z.array(ShipmentDestinationSummarySchema),
  chargeBreakdowns: z.array(ShipmentChargeBreakdownSchema),
  chargesCount: z.int(),
});

/**
 * Shipments RPC Contract
 */
export const shipmentsContracts = {
  get: oc
    .input(GetShipmentInputSchema)
    .output(GetShipmentResponseSchema)
    .route({
      summary: "Get shipment details",
      description:
        "Returns one shipment with its linked sales orders, purchase orders, destinations and freight charge breakdown with allocations.",
      method: "GET",
    }),
  list: oc
    .input(ListShipmentsInputSchema)
    .output(ListShipmentsResponseSchema)
    .route({
      summary: "List shipments",
      description:
        "Paged incoming or outgoing shipments, filterable by type, status and scheduled date range.",
      method: "GET",
    }),
} as const;

// Type exports
export type Shipment = z.infer<typeof ShipmentSchema>;
export type ShipmentType = z.infer<typeof ShipmentTypeSchema>;
export type ShipmentStatus = z.infer<typeof ShipmentStatusSchema>;
export type ShipmentSalesOrderSummary = z.infer<
  typeof ShipmentSalesOrderSummarySchema
>;
export type ShipmentPurchaseOrderSummary = z.infer<
  typeof ShipmentPurchaseOrderSummarySchema
>;
export type ShipmentChargeSummary = z.infer<typeof ShipmentChargeSummarySchema>;
export type ShipmentDestinationSummary = z.infer<
  typeof ShipmentDestinationSummarySchema
>;
export type ShipmentChargeAllocationSummary = z.infer<
  typeof ShipmentChargeAllocationSummarySchema
>;
export type ShipmentChargeBreakdown = z.infer<
  typeof ShipmentChargeBreakdownSchema
>;
export type ListShipmentsInput = z.infer<typeof ListShipmentsInputSchema>;
export type ListShipmentsResponse = z.infer<typeof ListShipmentsResponseSchema>;
export type GetShipmentInput = z.infer<typeof GetShipmentInputSchema>;
export type GetShipmentResponse = z.infer<typeof GetShipmentResponseSchema>;
